import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Checkbox } from "../../ui/checkbox";
import { Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GroupApprovalDialog = ({
  open,
  onOpenChange,
  groupApprovalRequest,
  groupApprovalData,
  setGroupApprovalData,
  setGroupApprovalRequest,
  clients,
  clientGroups,
  onSuccess  // Called after successful approval to refresh data
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  if (!groupApprovalRequest) return null;
  
  const requestClient = clients.find(c => c.id === groupApprovalRequest.client_id);
  const requestGroup = clientGroups.find(g => g.id === groupApprovalRequest.group_id);
  
  // Use client_name from the request as fallback when client not found in clients array
  const clientDisplayName = requestClient?.full_name || groupApprovalRequest.client_name || 'Unknown Client';
  const groupDisplayName = requestGroup?.name || groupApprovalRequest.group_name || 'Unknown Group';

  const handleCancel = () => {
    onOpenChange(false);
    setGroupApprovalRequest(null);
  };

  const handleApprove = async () => {
    if (isSubmitting) return; // Prevent double-click
    setIsSubmitting(true);
    try {
      await axios.put(`${API}/group-admission-requests/${groupApprovalRequest.id}`, {
        status: 'approved',
        review_notes: '',
        start_date: groupApprovalData.start_date,
        add_to_future_appointments: groupApprovalData.add_to_future_appointments
      });
      toast.success(`${clientDisplayName} added to ${groupDisplayName}`);
      onOpenChange(false);
      setGroupApprovalRequest(null);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Failed to approve:", error);
      toast.error("Failed to approve request");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Approve Group Admission</DialogTitle>
          <DialogDescription>
            Set the start date and options for this group admission.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm">
              <strong>Client:</strong> {clientDisplayName}
            </p>
            <p className="text-sm">
              <strong>Group:</strong> {groupDisplayName}
            </p>
          </div>
          
          <div>
            <Label htmlFor="approval_start_date">Start Date</Label>
            <Input
              id="approval_start_date"
              type="date"
              value={groupApprovalData.start_date}
              onChange={(e) => setGroupApprovalData({
                ...groupApprovalData,
                start_date: e.target.value
              })}
            />
            <p className="text-xs text-gray-500 mt-1">
              When should this member's participation begin?
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            <Checkbox
              id="add_to_future_appointments"
              checked={groupApprovalData.add_to_future_appointments}
              onCheckedChange={(checked) => setGroupApprovalData({
                ...groupApprovalData,
                add_to_future_appointments: checked
              })}
            />
            <label 
              htmlFor="add_to_future_appointments" 
              className="text-sm cursor-pointer"
            >
              Add to all scheduled future group sessions
            </label>
          </div>
          
          <div className="flex justify-end space-x-2 pt-4">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={handleApprove}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Approving...
                </>
              ) : (
                'Approve & Add to Group'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GroupApprovalDialog;
